import { useState, useEffect } from 'react';
import { SupportedLocale, DEFAULT_LOCALE, getUITexts, getFullQuestionPool, getDemoQuestions } from '../data';

const LOCALE_STORAGE_KEY = 'istqb-ai-quiz-locale';

export const useLocalization = () => {
  const [currentLocale, setCurrentLocale] = useState<SupportedLocale>(() => {
    // Try to get locale from localStorage first
    if (typeof window !== 'undefined') {
      const savedLocale = localStorage.getItem(LOCALE_STORAGE_KEY) as SupportedLocale;
      if (savedLocale && ['en', 'ru', 'uk', 'pl', 'ko'].includes(savedLocale)) {
        return savedLocale;
      }
    }
    
    // Always default to English for first-time users
    return DEFAULT_LOCALE; // 'en'
  });

  // Save locale to localStorage when it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(LOCALE_STORAGE_KEY, currentLocale);
    }
  }, [currentLocale]);

  const changeLocale = (newLocale: SupportedLocale) => {
    setCurrentLocale(newLocale);
    
    // Trigger haptic feedback on mobile
    if (navigator.vibrate && window.innerWidth < 768) {
      navigator.vibrate(10);
    }
    
    // Optional: trigger custom event for other components to listen to
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('localeChanged', { 
        detail: { locale: newLocale } 
      }));
    }
  };

  // Get localized content
  const uiTexts = getUITexts(currentLocale);
  const fullQuestionPool = getFullQuestionPool(currentLocale);
  const demoQuestions = getDemoQuestions(currentLocale);
  // getRandomQuestions function removed as it's not implemented in locale files

  // Helper functions for string interpolation
  const t = (key: string, params?: Record<string, string | number>) => {
    const keys = key.split('.');
    let value: any = uiTexts;
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    if (typeof value !== 'string') {
      console.warn(`Translation key "${key}" not found for locale "${currentLocale}"`);
      return key;
    }
    
    if (params) {
      return Object.entries(params).reduce((str, [param, val]) => {
        return str.replace(new RegExp(`\\{${param}\\}`, 'g'), String(val));
      }, value);
    }
    
    return value;
  };

  // Formatting helpers
  const formatQuestionProgress = (current: number, total: number) => {
    return t('a11y.questionProgress', { current: String(current), total: String(total) });
  };

  const formatResultsSummary = (score: number) => {
    return t('a11y.resultsSummary', { score: String(score) });
  };

  // RTL detection (for future Arabic/Hebrew support)
  const isRTL = ['ar', 'he', 'fa'].includes(currentLocale);

  return {
    currentLocale,
    changeLocale,
    uiTexts,
    fullQuestionPool,
    demoQuestions,
    t,
    formatQuestionProgress,
    formatResultsSummary,
    isRTL
  };
};

export default useLocalization; 